import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
} from 'firebase/firestore';
import { useAuth } from '../../../useAuth';
import { db } from '../../../firebase';
import './rewardsLedger.css';

const LEDGER_LIMIT = 150;

function formatWhen(ts) {
  if (!ts) return '—';
  try {
    const date = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatPhone(phone) {
  const d = String(phone || '').replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return phone || '—';
}

function formatPoints(delta) {
  const n = Number(delta);
  if (!Number.isFinite(n) || n === 0) return '0';
  return n > 0 ? `+${n.toLocaleString()}` : n.toLocaleString();
}

function typeLabel(type) {
  const map = {
    refund_clawback: 'Refund clawback',
    return_exchange_net: 'Return / exchange',
    return_clawback: 'Return clawback',
    return_recorded: 'Return recorded',
    refund_skipped: 'Refund skipped',
  };
  return map[type] || type || '—';
}

function RewardsLedger() {
  const { user, claims, loading: authLoading } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const isAdmin = claims.admin === true;

  useEffect(() => {
    if (authLoading) return undefined;
    if (!user || !isAdmin) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const q = query(
      collection(db, 'RewardsLedger'),
      orderBy('createdAt', 'desc'),
      limit(LEDGER_LIMIT)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEntries(rows);
        setLoading(false);
        setError('');
      },
      (err) => {
        console.error('RewardsLedger snapshot error:', err);
        setLoading(false);
        setError(
          'Could not load the rewards ledger. Deploy Firestore rules for RewardsLedger if needed, then refresh.'
        );
      }
    );

    return () => unsub();
  }, [authLoading, user, isAdmin]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const digits = search.replace(/\D/g, '');
    return entries.filter((row) => {
      if (typeFilter !== 'all' && row.type !== typeFilter) return false;
      if (!term && !digits) return true;
      const hay = [
        row.id,
        row.phone10,
        row.orderDocId,
        row.shopifyOrderId,
        row.refundId,
        row.returnId,
        row.type,
        row.source,
        row.reason,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (term && hay.includes(term)) return true;
      if (digits && String(row.phone10 || '').includes(digits)) return true;
      if (digits && String(row.shopifyOrderId || '').includes(digits)) return true;
      return Boolean(term && hay.includes(term));
    });
  }, [entries, search, typeFilter]);

  if (authLoading || loading) {
    return (
      <div className="rewards-ledger-page">
        <div className="rewards-ledger-header">
          <h1>Rewards ledger</h1>
        </div>
        <p className="rewards-ledger-status">Loading…</p>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="rewards-ledger-page">
        <div className="rewards-ledger-header">
          <h1>Rewards ledger</h1>
        </div>
        <p className="rewards-ledger-error">You need admin access to view this page.</p>
      </div>
    );
  }

  return (
    <div className="rewards-ledger-page">
      <div className="rewards-ledger-header">
        <div>
          <h1>Rewards ledger</h1>
          <p className="rewards-ledger-subtitle">
            Point adjustments from Shopify refunds and returns/exchanges. Newest first (last{' '}
            {LEDGER_LIMIT}).
          </p>
        </div>
      </div>

      <div className="rewards-ledger-toolbar">
        <input
          type="search"
          className="rewards-ledger-search"
          placeholder="Search phone, order, refund, return…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search ledger"
        />
        <select
          className="rewards-ledger-filter"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          aria-label="Filter by type"
        >
          <option value="all">All types</option>
          <option value="refund_clawback">Refund clawback</option>
          <option value="return_exchange_net">Return / exchange</option>
          <option value="return_clawback">Return clawback</option>
          <option value="return_recorded">Return recorded</option>
          <option value="refund_skipped">Refund skipped</option>
        </select>
      </div>

      {error ? <p className="rewards-ledger-error">{error}</p> : null}

      <div className="rewards-ledger-table-wrap">
        {filtered.length === 0 ? (
          <p className="rewards-ledger-empty">
            {entries.length === 0
              ? 'No ledger entries yet. They appear when refunds/create or returns/process run.'
              : 'No entries match your search.'}
          </p>
        ) : (
          <table className="rewards-ledger-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Type</th>
                <th>Customer</th>
                <th>Order</th>
                <th>Points</th>
                <th>Balance</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const delta = Number(row.pointsDelta) || 0;
                const detailBits = [];
                if (row.refundAmount != null) detailBits.push(`Refund $${Number(row.refundAmount).toFixed(2)}`);
                if (row.returnValue != null) detailBits.push(`Return $${Number(row.returnValue).toFixed(2)}`);
                if (row.exchangeValue != null) detailBits.push(`Exchange $${Number(row.exchangeValue).toFixed(2)}`);
                if (row.reason) detailBits.push(row.reason);
                if (row.refundId) detailBits.push(`Refund #${row.refundId}`);
                if (row.returnId) detailBits.push(`Return #${row.returnId}`);

                return (
                  <tr key={row.id}>
                    <td className="rewards-ledger-when">{formatWhen(row.createdAt)}</td>
                    <td>
                      <span className={`rewards-ledger-type rewards-ledger-type-${row.type || 'unknown'}`}>
                        {typeLabel(row.type)}
                      </span>
                      {row.source ? (
                        <span className="rewards-ledger-source">{row.source}</span>
                      ) : null}
                    </td>
                    <td>{formatPhone(row.phone10)}</td>
                    <td>
                      <span className="rewards-ledger-order">{row.orderDocId || '—'}</span>
                      {row.shopifyOrderId ? (
                        <span className="rewards-ledger-shopify-id">Shopify {row.shopifyOrderId}</span>
                      ) : null}
                    </td>
                    <td>
                      <span
                        className={`rewards-ledger-delta ${
                          delta > 0 ? 'is-credit' : delta < 0 ? 'is-debit' : ''
                        }`}
                      >
                        {formatPoints(delta)}
                      </span>
                    </td>
                    <td>
                      {row.pointsAfter != null ? Number(row.pointsAfter).toLocaleString() : '—'}
                    </td>
                    <td className="rewards-ledger-details">
                      {detailBits.length ? detailBits.join(' · ') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default RewardsLedger;
